import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { botServerApi } from '../services/api';

const REFRESH_INTERVAL_MS = 30000;

const unwrap = (data) => {
  const payload = data?.response ?? data?.data?.response ?? data?.data ?? data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

// unique_key is "<link>_<username>" — the same key the API uses to keep one POS
// account to a single transaction at a time. Split from the right: a link can
// contain underscores, a trailing cashier name is the last segment.
const splitKey = (uniqueKey) => {
  if (!uniqueKey) return { link: '—', account: '—' };
  const at = uniqueKey.lastIndexOf('_');
  if (at < 0) return { link: uniqueKey, account: '—' };
  return { link: uniqueKey.slice(0, at), account: uniqueKey.slice(at + 1) };
};

const relative = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  const s = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const ProfileAffinity = () => {
  const [rows, setRows] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const [affinity, serverList] = await Promise.all([
        botServerApi.getAffinity(),
        botServerApi.getServers(),
      ]);
      setRows(unwrap(affinity));
      setServers(unwrap(serverList));
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load the mapping');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  const serverName = useMemo(() => {
    const byId = {};
    servers.forEach((s) => { byId[s.id] = s; });
    return byId;
  }, [servers]);

  // Signing out releases the browser profile on the bot AND the session at
  // the POS, which is what frees a cashier that cannot sign in anywhere else
  // (and, on F2Go, the station it was holding).
  const logout = async (row) => {
    setSavingKey(row.uniqueKey);
    setNotice('');
    try {
      const res = await botServerApi.logoutSession({
        uniqueKey: row.uniqueKey,
        category: row.category,
      });
      const id = res?.data?.response?.id ?? res?.data?.id;
      setNotice(`Sign-out queued${id ? ` as task ${id}` : ''}. It runs on the machine holding the session.`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not queue the sign-out');
    } finally {
      setSavingKey('');
    }
  };

  const update = async (row, botServerId, pinned) => {
    setSavingKey(row.uniqueKey);
    try {
      await botServerApi.setAffinity({
        uniqueKey: row.uniqueKey,
        category: row.category,
        botServerId,
        pinned,
      });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not save');
    } finally {
      setSavingKey('');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Session routing</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
          Each bot keeps a logged-in browser profile per POS account, so a task sent to the
          machine that already holds one skips the login — worth about 48 seconds on River and
          12 on Golden Dragon Mobi. Tasks follow whichever server ran the account last; pin a
          row to send it somewhere else instead.
        </p>
      </div>

      {notice && (
        <div className="mb-4 rounded border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300">
          {notice}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              {['Game', 'Account', 'Link', 'Runs on', 'Last run', 'Routing'].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-gray-900">
            {loading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                No account has run yet, so there is nothing to route.
              </td></tr>
            )}
            {rows.map((row) => {
              const { link, account } = splitKey(row.uniqueKey);
              const current = row.botServerId ?? '';
              const busy = savingKey === row.uniqueKey;
              const offline = row.serverStatus && row.serverStatus !== 'ONLINE';
              return (
                <tr key={row.uniqueKey} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.category}</td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-900 dark:text-gray-100">{account}</td>
                  <td className="max-w-xs truncate px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400" title={link}>{link}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={offline ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}>
                      {row.serverName || '—'}
                    </span>
                    {offline && <span className="ml-2 text-xs text-red-600 dark:text-red-400">({row.serverStatus})</span>}
                    {row.pinned && (
                      <span className="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                        pinned
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {relative(row.lastRunAt)}
                    {row.pinned && row.lastRunServerId && row.lastRunServerId !== row.botServerId && (
                      <div className="text-xs text-amber-600 dark:text-amber-400">
                        profile still on {row.lastRunServerName || row.lastRunServerId}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <select
                        value={current}
                        disabled={busy}
                        onChange={(e) => update(row, Number(e.target.value), true)}
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                      >
                        <option value="">Select a server…</option>
                        {servers.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}{s.status !== 'ONLINE' ? ` (${s.status})` : ''}
                          </option>
                        ))}
                      </select>
                      {row.pinned && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => update(row, undefined, false)}
                          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                          Unpin
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => logout(row)}
                        title="Sign this cashier out and free the browser profile"
                        className="rounded border border-amber-400 px-2 py-1 text-xs text-amber-700 hover:bg-amber-50 disabled:opacity-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-950"
                      >
                        Sign out
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        Pinning does not move anything by itself. The logged-in profile stays on the old machine,
        so the next task logs in fresh on the new one and builds the profile there — one slow
        transaction, once. A pinned server that goes offline is skipped rather than waited for.
      </p>
    </div>
  );
};

export default ProfileAffinity;
